import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { delay } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Endpoints } from '../resources/endpoints';
import { PreBill, PreBillState } from '../interfaces/preBill';
import { DateManager } from '../managers/date.manager';
import { DateEnum } from '../enums/date.enum';
import { AuthService } from './authentication.service';
import { Utils } from '../resources/utils';
import { Cargo } from '../interfaces/cargo';
import { TransportCompany } from '../interfaces/transportCompany';
import { ResponseList } from '../interfaces/response-list';
import { PreBillFingerprintHistory } from '../interfaces/preBillFingerprintHistory';
import { PreBillPreview } from '../interfaces/preBillPreview';

export interface PreBillCargoFilters {
  dateFrom?: string;
  dateTo?: string;
  contractor?: string;
}

@Injectable({
  providedIn: 'root'
})
export class PreBillService {
  constructor(
    private _http: HttpClient,
    private _endpointResources: Endpoints,
    private authService: AuthService,
    private utils: Utils
  ) { }

  public getPreBills(
    state?: PreBillState,
    pageKey?: number,
    pageSize?: number,
    consecutive?: number,
    companyId?: string,
    validated?: boolean,
    transportCompanyId?: string
  ): Observable<ResponseList<PreBill>> {
    let params = new HttpParams();
    if (state) params = params.append('state', state);
    if (consecutive) params = params.append('consecutive', consecutive.toString());
    if (companyId) params = params.append('companyId', companyId);
    if (this.utils.isDefined(validated)) params = params.append('validated', validated.toString());
    if (transportCompanyId) params = params.append('transportCompanyId', transportCompanyId);
    if (pageKey && pageSize) {
      params = params.append('pageKey', pageKey.toString());
      params = params.append('pageSize', pageSize.toString());
    }
    return this._http.get<ResponseList<PreBill>>(
      `${environment.urlServerTeclogi}${this._endpointResources.urlPreBill}`,
      { params }
    );
  }

  public getPreBill(id: string): Observable<PreBill> {
    return this._http.get<PreBill>(
      `${environment.urlServerTeclogi}${this._endpointResources.urlPreBill}/${id}`
    );
  }

  public createPreBill(companyId: string, cargos: Cargo[], transportCompany?: TransportCompany): Observable<PreBill> {
    const body: { companyId: string, cargoIds: string[], transportCompany?: TransportCompany } = { companyId, cargoIds: cargos.map(cargo => cargo.id) };
    // Sin empresa de transporte (SaaS sin transporte) se envía como antes.
    if (transportCompany && transportCompany.id) body.transportCompany = transportCompany;
    return this._http.post<PreBill>(
      `${environment.urlServerTeclogi}${this._endpointResources.urlPreBill}`,
      body
    );
  }

  public updatePreBill(preBill: PreBill, cargos: Cargo[]): Observable<PreBill> {
    return this._http.put<PreBill>(
      `${environment.urlServerTeclogi}${this._endpointResources.urlPreBill}/${preBill.id}/cargos`,
      cargos.map(cargo => cargo.id)
    );
  }

  public validatePreBill(preBill: PreBill, validate: boolean): Observable<PreBill> {
    return this._http.put<PreBill>(
      `${environment.urlServerTeclogi}${this._endpointResources.urlPreBill}/${preBill.id}/validate?validated=${validate}`,
      {}
    );
  }

  public deletePreBill(preBill: PreBill): Observable<PreBill> {
    return this._http.delete<PreBill>(
      `${environment.urlServerTeclogi}${this._endpointResources.urlPreBill}/${preBill.id}`
    );
  }

  public getPreBillHistory(preBill: PreBill): Observable<PreBillFingerprintHistory[]> {
    return this._http.get<PreBillFingerprintHistory[]>(
      `${environment.urlServerTeclogi}${this._endpointResources.urlPreBill}/${preBill.id}/fingerprints`
    );
  }

  public getPreBillPreview(id: string): Observable<PreBillPreview> {
    return this._http.get<PreBillPreview>(
      `${environment.urlServerTeclogi}${this._endpointResources.urlPreBill}/${id}/preview`
    );
  }

  public downloadPreBillPreviewExcel(id: string): Observable<Blob> {
    return this._http.get(
      `${environment.urlServerTeclogi}${this._endpointResources.urlPreBill}/${id}/preview/excel`,
      { responseType: 'blob' }
    );
  }
}
